import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { filter, map, merge, of, Subject, switchMap, take, tap } from 'rxjs';

import { ConfirmComponent } from './components/confirm/confirm.component';
import { ModalComponent } from './components/modal/modal.component';

import type { OverlayRef } from '@angular/cdk/overlay';
import type { Type } from '@angular/core';
import type { Observable } from 'rxjs';

const infos = new Map<string, string>();
type ModalComponentInstance = ConfirmComponent | ModalComponent;
type ModalResult<T> = boolean | T | undefined;
type ModalType = 'confirm' | 'info';

@Service()
export class ModalService {
  readonly #httpß = inject(HttpClient);
  readonly #overlay = inject(Overlay);

  #createOverlay(): OverlayRef {
    return this.#overlay.create({
      backdropClass: ['bg-black', 'bg-black/70'],
      hasBackdrop: true,
      panelClass: 'mx-4',
      positionStrategy: this.#overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      scrollStrategy: this.#overlay.scrollStrategies.block(),
    });
  }

  // oxlint-disable-next-line max-params
  confirm(
    title: string,
    question: string,
    justOk = false,
    okBtn?: string,
    noBtn?: string,
  ): Observable<boolean> {
    return this.openModal({ justOk, noBtn, okBtn, question, title }, 'confirm');
  }

  openHtmlInfo(path: string): void {
    (infos.has(path)
      ? of(infos.get(path))
      : this.#httpß.get(`/info/${path}.html`, { responseType: 'text' })
    )
      .pipe(
        tap(html => {
          infos.set(path, html || '');
        }),
        switchMap(html => this.openModal({ html })),
      )
      // eslint-disable-next-line @smarttools/rxjs/no-ignored-subscribe
      .subscribe();
  }

  openModal<T = unknown>(payload: Record<string, unknown>): Observable<T>;
  openModal(
    payload: Record<string, unknown> & { component?: Type<unknown> },
    type: 'confirm',
  ): Observable<boolean>;
  openModal<T = unknown>(
    payload?: Record<string, unknown>,
    type: ModalType = 'info',
  ): Observable<ModalResult<T>> {
    const overlayRef = this.#createOverlay();

    const modalCmp = new ComponentPortal<ModalComponentInstance>(
      type === 'info' ? ModalComponent : ConfirmComponent,
    );
    const modal = overlayRef.attach(modalCmp);

    const close$ = new Subject<unknown>();
    const result$ = new Subject<ModalResult<T>>();

    Object.assign(modal.instance, { close$, ...payload });

    merge(
      close$ as Observable<ModalResult<T>>,
      overlayRef.backdropClick().pipe(
        filter(() => type === 'info'),
        map(() => undefined),
      ),
    )
      .pipe(
        take(1),
        tap(() => {
          overlayRef.dispose();
        }),
      )
      .subscribe(result$);

    return result$.asObservable().pipe(take(1));
  }
}
